import { useEffect, useMemo, useRef, useState } from "react";
import {
  cupArc, finish, goTo, initialState, markAll, next, screen, step, stepsLayout, tick, type AC, type Fill, type Screen,
  type Walk, type WalkState,
} from "../lib/playerWalk";
import { DIFFICULTY_LABELS, NOTE_LABEL_COACHED, RAIL, SAVE_TITLE, SET_MARK, SPINE, STEPS, WORDS } from "../lib/playerFacts";
import { FORMAT_LABEL, type SectionFormat } from "../lib/builder";
import { SPACING, phoneThumbnail } from "../lib/briefing";
import { PHONE_POINTS } from "../lib/phoneOverview";
import type { Media } from "./PhoneOverview";
import { SET_TABLE } from "../lib/phoneSetTable";

/* 🔴 W130 — "START WORKOUT" IN THE COACH'S PHONE PREVIEW (his 2026-10-01 ~22:55 word: *"always have a start workout button and
 * actually the coach see how it will be done in real … if a coach wants to see he doesn't need to create a client
 * account"*). The client's workout walked inside the same device frame: the pages in the pager's order, working ticks, the
 * superset's round steps, the W / C steps, the rail, `Next exercise ›`, the END page and the finish page.
 *
 * NOTHING IS SAVED: every decision is `lib/playerWalk.ts` (pure; held equal to the phone's own rules by a Swift-emitted
 * corpus), the state lives in the caller's React state and is dropped when the dialog closes. This file imports no client,
 * no store and no network (a vitest ruler and the harness gate assert it).
 *
 * The FIRST CUT, said on the page where it differs from the phone: no rest clock (the phone turns the page when the rest
 * ends; here `Next exercise ›` does), no media playback, no tools row, no hold timer, and a section the app runs on a clock
 * is one page that says so. Sizes are phone POINTS (`--pt`), the W66 frame's unit. */

export type PlayerView = { st: WalkState; finished: boolean; rating: number | null; note: string };
export function newPlayerView(w: Walk): PlayerView { return { st: initialState(w), finished: false, rating: null, note: "" }; }

const CONTENT_WIDTH = PHONE_POINTS.width - 2 * SPACING.md;
/** The web's ONE line of its own (the brief's words): a section the app runs on a clock is not played in this cut. */
export const TIMED_LINE = "The app runs this one on a clock";
/** How far a drag must travel to turn the page — a swipe, not a wobble. */
const SWIPE_PX = 48;

const TABLE_VARS = {
  "--st-set": SET_TABLE.setW, "--st-rest": SET_TABLE.restW, "--st-tick": SET_TABLE.tickW, "--st-row": SET_TABLE.rowMinHeight,
  "--st-head": SET_TABLE.headerSize, "--st-head-track": SET_TABLE.headerTracking, "--st-label": SET_TABLE.setLabelSize,
  "--st-number": SET_TABLE.numberSize, "--st-rest-text": SET_TABLE.restSize, "--st-tick-size": SET_MARK.diameter,
  "--st-mark": SET_TABLE.markAllSize,
} as React.CSSProperties;

export function PhonePlayer({ walk, view, onView, media, onBack }: {
  walk: Walk;
  view: PlayerView;
  onView: (v: PlayerView) => void;
  /** The library's pictures, by LIBRARY id (the overview's own read). Only stills are drawn — no playback in this cut. */
  media: ReadonlyMap<string, Media>;
  /** Back to the overview, the walk kept (the phone's back chevron). */
  onBack: () => void;
}) {
  const sc = useMemo(() => screen(walk, view.st, CONTENT_WIDTH), [walk, view.st]);
  const body = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  /* Every page opens at its top (his 2026-09-09 ask on the phone), and focus moves to the page's back control so a keyboard
     user lands at the head of the new page. */
  useEffect(() => { body.current?.scrollTo({ top: 0 }); }, [view.st.page, view.finished]);
  useEffect(() => { backRef.current?.focus(); }, [view.finished]);

  const set = (st: WalkState) => { onView({ ...view, st }); };
  const exIndexOf = (sectionIdx: number, id: string) => walk.model.sections[sectionIdx]!.exercises.findIndex((e) => e.id === id);
  const page = walk.pages[view.st.page];

  if (view.finished) {
    return <FinishPage walk={walk} view={view} onView={onView} media={media} backRef={backRef}
                       onSave={() => { onBack(); }} />;
  }

  const timedName = sc.timed === null ? null : FORMAT_LABEL[sc.timed.format as SectionFormat] ?? sc.timed.format;
  return (
    <div className="pp" style={TABLE_VARS}
         onKeyDown={(e) => {
           // The pager's swipe on a keyboard: ← / → turn the page, as a thumb does (never while typing).
           if ((e.target as HTMLElement).tagName === "TEXTAREA") return;
           if (e.key === "ArrowRight") { e.preventDefault(); set(goTo(walk, view.st, view.st.page + 1)); }
           if (e.key === "ArrowLeft") { e.preventDefault(); set(goTo(walk, view.st, view.st.page - 1)); }
         }}>
      <header className="pp-head">
        <div className="pp-head-row">
          <button type="button" ref={backRef} className="pp-back" aria-label="Back to workout overview" onClick={onBack}>
            <Chevron />
          </button>
          {/* W141: on a warm-up / cool-down page the back row carries the section's title alone (`headTitle`). */}
          <p className="pp-headline">
            <span className="pp-headline-name">{sc.head.headTitle ?? sc.head.name.toUpperCase()}</span>
            {sc.head.headTitle === null && <span className="pp-headline-tail">{sc.head.tail}</span>}
          </p>
        </div>
        <Rail sc={sc} />
        {sc.steps !== null && (
          <Steps title={sc.steps.title} labels={sc.steps.labels} fills={sc.steps.fills} current={sc.steps.current}
                 spoken={(i) => `${sc.steps!.kind === "warmUp" ? "Warm-up" : "Cool-down"}, exercise ${String(i + 1)} of ${String(sc.steps!.labels.length)}`}
                 onTap={(i) => { set(step(walk, view.st, i)); }} />
        )}
      </header>

      <div className="pp-scroll" ref={body}
           onPointerDown={(e) => { swipe.current = { x: e.clientX, y: e.clientY }; }}
           onPointerUp={(e) => {
             const s = swipe.current; swipe.current = null;
             if (s === null) return;
             const dx = e.clientX - s.x, dy = e.clientY - s.y;
             if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > 2 * Math.abs(dy)) set(goTo(walk, view.st, view.st.page + (dx < 0 ? 1 : -1)));
           }}
           onPointerCancel={() => { swipe.current = null; }}>
        {sc.end && sc.endPage !== null && (
          <EndPage done={sc.endPage.done} count={sc.endPage.count} canEnd={sc.endPage.canEnd}
                   onEnd={() => { onView({ ...view, finished: true }); }} />
        )}
        {sc.timed !== null && (
          <section className="pp-timed" aria-labelledby="pp-timed-title">
            <p className="pp-overline">{timedName}</p>
            <h3 className="pp-title" id="pp-timed-title">{sc.timed.name || sc.head.name}</h3>
            <p className="pp-timed-line">{TIMED_LINE}</p>
          </section>
        )}
        {sc.roundSteps !== null && (
          <Steps title={sc.roundSteps.title} labels={sc.roundSteps.labels} fills={sc.roundSteps.fills} current={sc.roundSteps.current}
                 spoken={(i) => `Superset, round ${String(i + 1)} of ${String(sc.roundSteps!.labels.length)}`}
                 onTap={(i) => { set(step(walk, view.st, i)); }} />
        )}
        {page !== undefined && page.kind !== "timed" && sc.tables.map((t) => {
          const e = walk.model.sections[page.section]!.exercises.find((x) => x.id === t.exercise)!;
          const m = e.exerciseId === null ? undefined : media.get(e.exerciseId);
          const src = m === undefined ? null : phoneThumbnail(m.demo, m.thumb);
          const ei = exIndexOf(page.section, t.exercise);
          const isRound = page.kind === "round";
          return (
            <article key={t.exercise} className={isRound ? "pp-card pp-member" : "pp-exercise"}>
              <div className={isRound ? "pp-member-head" : ""}>
                <div className={isRound ? "pp-thumb" : "pp-video"} aria-hidden="true">
                  {src !== null ? <><img src={src} alt="" /><span className="pp-play">▶</span></>
                    : isRound ? <span className="pp-mono">{t.name.trim().charAt(0).toUpperCase()}</span> : <span className="pp-novideo">No video yet</span>}
                </div>
                <div className="pp-titles">
                  <h3 className="pp-title">{t.name || "Untitled exercise"}</h3>
                  {t.tempo.trim() !== "" && <ul className="pp-chips"><li className="pp-chip">Tempo {t.tempo.trim()}</li></ul>}
                </div>
              </div>
              {t.note !== "" && <p className="pp-note">{t.note}</p>}
              <div className={isRound ? "pp-table" : "pp-card pp-table"} role="table" aria-label={`${t.name} sets`}>
                <div className="px-row px-head" role="row">
                  {t.columns.map((c) => <span key={c} role="columnheader" className={`px-cell px-${c.toLowerCase()}`}>{c}</span>)}
                  <span className="px-cell px-tickcol" role="columnheader"><span className="ht-visually-hidden">Done</span></span>
                </div>
                {t.rows.map((r) => (
                  <div key={r.set} role="row" className={`px-row${r.live ? " is-live" : ""}${r.logged ? " is-logged" : ""}`}>
                    {t.columns.map((c, ci) => (
                      <span key={c} role="cell" className={`px-cell px-${c.toLowerCase()}`}>
                        {c === "SET" ? r.label : c === "REST" ? r.rest : (r.cells[ci - 1] ?? "")}
                      </span>
                    ))}
                    <span className="px-cell px-tickcol" role="cell">
                      <button type="button" className={`pp-tick${r.logged ? " is-done" : ""}`} aria-pressed={r.logged}
                              aria-label={`${r.logged ? "Unmark" : "Mark"} ${r.label === "W" || r.label === "D" || r.label === "F" ? `${r.label} set` : `set ${r.label}`} of ${t.name}`}
                              onClick={() => { set(tick(walk, view.st, ei, r.set)); }}>
                        {r.logged && <TickMark />}
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
        {sc.markAll !== null && (
          <button type="button" className="pp-markall" onClick={() => { set(markAll(walk, view.st)); }}>{sc.markAll}</button>
        )}
        <div className="pp-clearance" aria-hidden="true" />
      </div>

      {sc.capsule !== null && (
        <button type="button" className="pp-capsule" onClick={() => { set(next(walk, view.st)); }}>
          {sc.capsule} <span aria-hidden="true">›</span>
        </button>
      )}
    </div>
  );
}

/** The head's track (`PlayerRail`, W141): the line segment by segment (solid behind, dashed ahead) running at full ink to a
 *  half circle round each glyph that fades out past the verticals, a dot per exercise, the current one larger, one curve
 *  over and one under each superset, the flame and the snowflake. Display only. */
function Rail({ sc }: { sc: Screen }) {
  const { xs, entries, current, segments, curves } = sc.rail;
  const y = RAIL.lineY, w = CONTENT_WIDTH;
  const gid = useMemo(() => `pp-rail-${Math.random().toString(36).slice(2, 8)}`, []);
  // (along, across) → the SVG's (x, y): across is measured away from the line on the curve's side, forward = over it.
  const P = (p: AC, side: 1 | -1) => `${String(p[0])},${String(y - side * p[1])}`;
  const isGlyph = (i: number) => entries[i] === "warmUp" || entries[i] === "coolDown";
  // The cups: a glyph end of a DRAWN segment, facing along it (0° = +x), in that segment's state.
  const cups: { cx: number; facing: number; solid: boolean }[] = [];
  segments.forEach((s, i) => {
    if (!s.drawn) return;
    const solid = s.style === "solid";
    if (isGlyph(i)) cups.push({ cx: xs[i]!, facing: 0, solid });
    if (isGlyph(i + 1)) cups.push({ cx: xs[i + 1]!, facing: 180, solid });
  });
  return (
    <div className="pp-rail" role="img" aria-label={sc.head.spoken}>
      <svg viewBox={`0 0 ${String(w)} ${String(RAIL.height)}`} aria-hidden="true">
        {sc.rail.windowed && <defs><clipPath id={`${gid}-win`}><rect x={sc.rail.regionLo - 7} y={0} width={sc.rail.regionHi - sc.rail.regionLo + 14} height={RAIL.height} /></clipPath></defs>}
        <g className="pp-rail-ink">
          {segments.map((s, i) => {
            if (!s.drawn) return null;
            const a = xs[i]!;
            return <line key={i} x1={a + s.drawFrom} x2={a + s.drawTo} y1={y} y2={y}
                         stroke="currentColor" strokeWidth={s.style === "solid" ? 2.2 : 1.5}
                         strokeDasharray={s.style === "dashed" ? `${String(SPINE.dash)} ${String(SPINE.dash)}` : undefined}
                         strokeDashoffset={s.style === "dashed" ? s.phase : undefined} />;
          })}
          <g clipPath={sc.rail.windowed ? `url(#${gid}-win)` : undefined}>
            {entries.map((k, i) => (k === "warmUp" || k === "coolDown") ? null : (
              <circle key={i} cx={xs[i]} cy={y} r={i === current ? SPINE.liveRadius : SPINE.dotRadius}
                      className={i === current ? "pp-rail-live" : undefined} fill="currentColor" />
            ))}
            {curves.map((c, i) => (
              <g key={i}>
                <path d={`M${P(c.start, c.side)} Q${P(c.control, c.side)} ${P(c.strokeEnd, c.side)}`} fill="none" stroke="currentColor"
                      strokeWidth={1.5} strokeLinecap="round" />
                <path d={`M${P(c.tip, c.side)} L${P(c.left, c.side)} L${P(c.right, c.side)} Z`} fill="currentColor" />
              </g>
            ))}
          </g>
        </g>
        {entries.map((k, i) => k === "warmUp" ? <Flame key={i} x={xs[i]!} y={y} live={i === current} />
          : k === "coolDown" ? <Snowflake key={i} x={xs[i]!} y={y} live={i === current} /> : null)}
      </svg>
      {cups.map((c, i) => <Cup key={i} {...c} y={y} />)}
    </div>
  );
}

/** `SpineTrack.cup` (W141): the half circle round a glyph on the side facing its neighbour, `cupOverrun`° past each
 *  vertical, its ink full where the line joins and fading out by `SPINE.cupFade` — a CONIC mask about the glyph's centre,
 *  as the phone's conic gradient: the opacity follows the angle, the same both sides. A ring, so the stroke is centred on
 *  `cupRadius` exactly as SwiftUI strokes the arc. */
function Cup({ cx, y, facing, solid }: { cx: number; y: number; facing: number; solid: boolean }) {
  const sw = solid ? 2.2 : 1.5, R = SPINE.cupRadius, box = 2 * R + sw;
  const arc = cupArc(facing);
  const half = (arc.to - arc.from) / 2;   // 90 + overrun: the arc's reach off the facing, each side
  const ink = (o: number) => `rgba(0,0,0,${String(o)})`;
  const plus = [[0, 1] as const, ...SPINE.cupFade].map(([off, o]) => `${ink(o)} ${String(off)}deg`);
  const minus = [...SPINE.cupFade].reverse().map(([off, o]) => `${ink(o)} ${String(360 - off)}deg`);
  // CSS's conic 0° is up; the phone's 0° is +x — so the facing sits at `facing + 90` here.
  const mask = `conic-gradient(from ${String(facing + 90)}deg, ${plus.join(", ")}, transparent ${String(half)}deg, transparent ${String(360 - half)}deg, ${minus.join(", ")}, ${ink(1)} 360deg)`;
  return <span className="pp-rail-cup pp-rail-ink" aria-hidden="true"
               style={{ "--pp-cup-x": cx - box / 2, "--pp-cup-y": y - box / 2, "--pp-cup-d": box, "--pp-cup-sw": sw, maskImage: mask, WebkitMaskImage: mask } as React.CSSProperties} />;
}

/* W141, his word: the flame *"a little smaller so it's looking same size like the snowflake"* — the phone draws it at
 * `RAIL.flameSize` (15) beside the snowflake's 16. MEASURED on the phone's 3× stills (W141 `glyphs.py`): the flame's ink is
 * 17.33 pt tall per 16 pt of font; this path's own ink is 15.62 tall (its arc's foot) — so it is scaled to the phone's ink. */
const FLAME_INK_PER_PT = 17.33 / 16;
const FLAME_PATH_INK = 15.62;
const FLAME_SCALE = (FLAME_INK_PER_PT * RAIL.flameSize) / FLAME_PATH_INK;
function Flame({ x, y, live }: { x: number; y: number; live: boolean }) {
  const s = (live ? 1.1 : 1) * FLAME_SCALE;
  return <path className="pp-flame" transform={`translate(${String(x)} ${String(y)}) scale(${String(s)}) translate(-7 -8)`}
               d="M7 0c.6 2.6 3.2 4.1 4.6 6.5 1.6 2.8 1 6.2-1.4 8.1A6.6 6.6 0 0 1 .8 12C.2 9.6 1 7.4 2.6 5.8c.2 1.4 1 2.4 2 2.8C4.2 5.6 5 2.6 7 0z" />;
}
function Snowflake({ x, y, live }: { x: number; y: number; live: boolean }) {
  const s = live ? 1.1 : 1;
  return (
    <g className="pp-snow" transform={`translate(${String(x)} ${String(y)}) scale(${String(s)})`} fill="none" strokeWidth={1.6} strokeLinecap="round">
      {[0, 60, 120].map((r) => <path key={r} transform={`rotate(${String(r)})`} d="M0 -7.5V7.5M-2.6 -5.5 0 -3.6 2.6 -5.5M-2.6 5.5 0 3.6 2.6 5.5" />)}
    </g>
  );
}

/** One steps row (`SupersetSteps`): ✓ all, ◐ some, the label none; the box on screen outlined in the accent. Real buttons. */
function Steps({ title, labels, fills, current, onTap, spoken }: {
  title: string | null; labels: string[]; fills: Fill[]; current: number; onTap: (i: number) => void; spoken: (i: number) => string;
}) {
  /* `W84Steps.layout`: up to eight boxes, each in a slot of at most 44 pt, at most 28 pt apart, centred; more keep that
     spacing and the row scrolls. The line runs centre to centre behind the boxes, ink once the box before it is done. */
  const fit = stepsLayout(labels.length, CONTENT_WIDTH, STEPS.maxSlot);
  return (
    <div className="pp-steps">
      {title !== null && <p className="pp-steps-title" aria-hidden="true">{title}</p>}
      <ol className={`pp-steps-row${fit.scrolls ? " is-scroll" : ""}`}
          style={{ "--pp-slot": fit.slot, "--pp-gap": fit.gap } as React.CSSProperties}>
        {labels.map((l, i) => (
          <li key={i} className={`pp-step-slot${i > 0 ? (fills[i - 1] === "all" ? " has-line is-inked" : " has-line") : ""}`}>
            <button type="button" className={`pp-step is-${fills[i] ?? "none"}${i === current ? " is-current" : ""}`}
                    aria-label={spoken(i)} aria-current={i === current ? "step" : undefined}
                    aria-description={fills[i] === "all" ? "done" : fills[i] === "some" ? "part done" : "not done"}
                    onClick={() => { onTap(i); }}>
              {fills[i] === "all" ? <TickMark /> : fills[i] === "some" ? <span className="pp-half" aria-hidden="true" /> : l}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** The END page (`WorkoutEndPage`): the phone's swipe, drawn as a control a click, a tap or Enter / Space works. */
function EndPage({ done, count, canEnd, onEnd }: { done: number; count: number; canEnd: boolean; onEnd: () => void }) {
  return (
    <section className="pp-end" aria-labelledby="pp-end-title">
      <span className="pp-end-flag" aria-hidden="true">⚑</span>
      <h3 className="pp-end-title" id="pp-end-title">
        You have completed <strong>{done} of {count}</strong> {count === 1 ? "exercise" : "exercises"}
      </h3>
      <p className="pp-end-line">{done < count ? WORDS.endSome : WORDS.endAll}</p>
      <button type="button" className="pp-swipe" disabled={!canEnd} onClick={onEnd}
              aria-label={canEnd ? "End workout" : "End workout, not available yet"}>
        <span className="pp-swipe-knob" aria-hidden="true">»</span>
        <span>{canEnd ? WORDS.swipe : WORDS.notReady}</span>
      </button>
      {!canEnd && <p className="pp-end-hint">{WORDS.floor}</p>}
    </section>
  );
}

/** The finish page (W133): `WORKOUT DONE`, the three tiles, HOW WAS IT?, the note, a card per section, `Save workout`. Time
 *  is `–` — nothing was timed in a preview, and a made-up duration would be a fabrication. Rows open nothing in this cut. */
function FinishPage({ walk, view, onView, media, backRef, onSave }: {
  walk: Walk; view: PlayerView; onView: (v: PlayerView) => void; media: ReadonlyMap<string, Media>;
  backRef: React.RefObject<HTMLButtonElement>; onSave: () => void;
}) {
  const f = useMemo(() => finish(walk, view.st), [walk, view.st]);
  const [ratingId] = useState(() => `pp-rate-${Math.random().toString(36).slice(2, 8)}`);
  const label = view.rating === null ? WORDS.slideToRate : DIFFICULTY_LABELS[view.rating - 1]!;
  return (
    <div className="pp pp-finish">
      <header className="pp-head">
        <div className="pp-head-row">
          <button type="button" ref={backRef} className="pp-back" aria-label="Back into the workout"
                  onClick={() => { onView({ ...view, finished: false }); }}><Chevron /></button>
        </div>
      </header>
      <div className="pp-scroll">
        <p className="pp-overline pp-done-word">{WORDS.workoutDone}</p>
        <h3 className="pp-title pp-finish-title">{walk.model.workoutName}</h3>
        <div className="pp-tiles">
          <Tile label="Time" value="–" unit="" />
          <Tile label="Sets" value={String(f.summary.setsLogged)} unit={`of ${String(f.summary.setsPrescribed)}`} />
          <Tile label="Exercises" value={String(f.summary.exercisesDone)} unit={`of ${String(f.summary.exercisesTotal)}`} />
        </div>
        <section className="pp-card pp-fcard" aria-labelledby={ratingId}>
          <p className="pp-card-head" id={ratingId}>{WORDS.howWasIt.toUpperCase()}</p>
          <p className={`pp-rate-word${view.rating === null ? " is-empty" : ""}`} aria-hidden="true">{label}</p>
          {/* The phone's 10-11 rating fix (`fc13c617`), mirrored: unrated, the thumb only RESTS on stop 3 and the bar is grey
              until an answer exists; every word is a tap that answers (a click on the resting stop fires no change). The range
              carries the answer for the keyboard and screen readers; the words are its pointer twin, out of the tab order. */}
          {/* 10-16 (the hover row's brief, rendered in DARK first): the native bar painted its unfilled half in the dark
              scheme's bright track — unrated it read grey-then-WHITE, not grey. The track is ours now: its fill runs to the
              thumb (`--pp-rate-at`), grey until answered, the accent after; the rest is the border grey in both themes. */}
          <input type="range" min={1} max={5} step={1} value={view.rating ?? 3}
                 className={`pp-rate${view.rating === null ? " is-empty" : ""}`}
                 style={{ "--pp-rate-at": `${String(((view.rating ?? 3) - 1) * 25)}%` } as React.CSSProperties}
                 aria-label="How was the workout" aria-valuetext={view.rating === null ? "Not rated" : label}
                 onChange={(e) => { onView({ ...view, rating: Number(e.target.value) }); }} />
          <div className="pp-rate-stops" aria-hidden="true">
            {DIFFICULTY_LABELS.map((d, i) => (
              <button key={d} type="button" tabIndex={-1} className={view.rating === i + 1 ? "is-on" : ""}
                      onClick={() => { onView({ ...view, rating: i + 1 }); }}>{d}</button>
            ))}
          </div>
        </section>
        <section className="pp-card pp-fcard">
          <label className="pp-card-head" htmlFor="pp-note">{NOTE_LABEL_COACHED.toUpperCase()}</label>
          <textarea id="pp-note" className="pp-note-field" rows={2} placeholder={WORDS.noteHint} value={view.note}
                    onChange={(e) => { onView({ ...view, note: e.target.value }); }} />
        </section>
        {f.sections.map((s, si) => (
          <section key={si} className="pp-card pp-fcard">
            <p className="pp-card-head">{s.label.toUpperCase()}</p>
            <ul className="pp-frows">
              {s.rows.map((r) => {
                const e = walk.model.sections[si]!.exercises.find((x) => x.id === r.exercise);
                const m = e?.exerciseId == null ? undefined : media.get(e.exerciseId);
                const src = m === undefined ? null : phoneThumbnail(m.demo, m.thumb);
                return (
                  <li key={r.exercise} className="pp-frow">
                    <span className="pp-fthumb" aria-hidden="true">{src !== null ? <img src={src} alt="" /> : r.name.trim().charAt(0).toUpperCase()}</span>
                    <span className="pp-frow-text"><span className="pp-frow-name">{r.name}</span><span className="pp-frow-line">{r.line}</span></span>
                    <span className={`pp-fmark is-${r.mark}`} role="img"
                          aria-label={r.mark === "done" ? "Done" : r.mark === "partial" ? "Part done" : r.mark === "skipped" ? "Skipped" : "Not logged"}>
                      {r.mark === "done" ? <TickMark /> : r.mark === "partial" ? <span className="pp-half" /> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        <div className="pp-clearance" aria-hidden="true" />
      </div>
      <button type="button" className="pp-capsule" onClick={onSave}>{SAVE_TITLE}</button>
    </div>
  );
}

function Tile({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div className="pp-tile">
      <span className="pp-tile-label">{label}</span>
      <span className="pp-tile-value">{value}{unit !== "" && <span className="pp-tile-unit"> {unit}</span>}</span>
    </div>
  );
}

function Chevron() {
  return <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3 5 8l5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function TickMark() {
  return <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.5 6.5 11.5 12.5 4.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
